import { type ReactNode, useCallback, useEffect, useState } from "react";
import { PHASE } from "../../../../../common/constants.ts";
import {
  getDashboardContractStatus,
  type DashboardContractStatus,
} from "../../../../../common/dashboardContractStatus.ts";
import getWinner from "../../../../../common/getWinner.ts";
import { AchievementsSheet } from "../../components/AchievementsSheet.tsx";
import { InboxSheet } from "../../components/InboxSheet.tsx";
import { LeagueNewsTicker } from "../../components/LeagueNewsTicker.tsx";
import { OwnerLetterDialog } from "../../components/OwnerLetterDialog.tsx";
import { MobileStatusMetric } from "../../components/MobileStatusMetric.tsx";
import { TeamLogoInline } from "../../components/TeamLogoInline.tsx";
import { TeamScoreValue } from "../../components/TeamScoreValue.tsx";
import useTitleBar from "../../hooks/useTitleBar.tsx";
import { homeFields } from "../../mobile/displayPolicies.ts";
import {
  MobileContentHero,
  MobileStatRow,
  mobileActionButtonClassNames,
} from "../../mobile/mobilePrimitives.tsx";
import { MobileStatInsights } from "../../mobile/MobileStatInsights.tsx";
import {
  getDashboardStatRankInsights,
  type DashboardStatRankLike,
} from "../../mobile/statInsights.ts";
import {
  GAME_ATTRIBUTION_DEFAULTS,
  getGameAttributionVariant,
} from "../../mobile/gameAttributionText.ts";
import { getCol } from "../../util/getCol.ts";
import { helpers } from "../../util/helpers.ts";
import { formatSeasonRecord } from "../../util/seasonRecord.ts";
import { createMobileCrossTabHref } from "../../util/mobileCrossTabLink.ts";
import i18n from "../../util/i18n.ts";
import { useLocalPartial } from "../../util/local.ts";
import { toWorker } from "../../util/toWorker.ts";
import { classifyInboxMessage } from "../../views/messageDisplay.ts";
import { ordinalLocalized } from "../../util/ordinalLocalized.ts";
import { translateRoundsWonText } from "../../util/roundsWonText.ts";
import { translateTeamName, translateTeamRegion } from "../../util/teamNames.ts";
import { formatOvertimeSummary } from "../../util/basketballLabels.ts";
import type { Option, View } from "../../../../../common/types.ts";
import { PlayOptionsMenu } from "./PhaseHighlight.tsx";
import { getDashboardRouteIntent } from "./dashboardRouteIntent.ts";
import {
  getDashboardFallbackPrimaryTask,
  getDashboardGoalAdvice,
  getDashboardOwnerTrustState,
  getDashboardPhaseChecklist,
  getDashboardSeasonGoalState,
  getDashboardTaskHub,
  getDashboardTodoSource,
  type DashboardOwnerMood,
  type DashboardHomeFocus,
  type DashboardRiskAlert,
  type DashboardSeasonGoalState,
  type DashboardTaskHub,
  type DashboardTaskItem,
  type PhaseTask,
} from "./primaryAction.ts";
import type { OwnerExpectations } from "../../../../../common/types.ts";
import {
  clearAdvanceSnapshot,
  computeAdvanceDigest,
  getDashboardDayDone,
  readAdvanceSnapshot,
  writeAdvanceSnapshot,
  type AdvanceDigest,
} from "./advanceDigest.ts";
import {
  achievementPromptEmitter,
  clearAchievementPrompt,
  readAchievementPrompt,
} from "../../util/achievementSession.ts";
import {
  getCapSpaceStatus,
  getContractStatusScore,
  getHealthScore,
  getOwnerTrustScore,
  getRosterStatus,
  getSeasonGoalScore,
  getTeamStrengthStatus,
} from "./statusMetrics.ts";

type SeasonExpectation = OwnerExpectations & { mid: number };

type Message = {
  from: string;
  mid: number;
  read?: boolean;
  subject?: string;
  text?: string;
  year: number;
};

type TeamSnapshot = {
  capSpace?: number;
  payroll?: number;
  payrollLimit?: number;
  rank?: number;
  record: {
    lost: number;
    otl: number;
    tied: number;
    won: number;
  };
  rosterCount?: number;
  teamOvr?: number;
};

type DashboardNextGame = {
  day?: number;
  daysUntil?: number;
  finals?: boolean;
  forceWin?: number | "tie";
  gid: number;
  season?: number;
  teams: [
    { lost?: number; ovr?: number; tid: number; won?: number },
    { lost?: number; ovr?: number; tid: number; won?: number },
  ];
};

type DashboardLastGameTeam = {
  pts: number;
  sPts?: number;
  tid: number;
};

type DashboardLastGame = {
  // G3-1: "your decision" credit for a recently traded-for / signed player who
  // suited up (worker-derived, read-only). Absent when no new arrival qualifies.
  attribution?: {
    type: "trade" | "freeAgent";
    name: string;
    debut: boolean;
    stats: { stat: string; value: number }[];
  };
  gid: number;
  keyPlayer?: {
    name: string;
    pid: number;
    pos?: string;
    stats: { stat: string; value: number }[];
  };
  numPeriods?: number;
  overtimes?: number;
  season: number;
  teams: [DashboardLastGameTeam, DashboardLastGameTeam];
};

// M1-3: one recent league-news row. Compact by design — the home rows and the
// modal both render plain event text, so the server ships text/type/season only.
type DashboardNewsItem = {
  eid: number;
  season: number;
  text: string;
  tids?: number[];
  type: string;
};

type LeagueDashboardProps = View<"leagueDashboard"> & {
  contractExpiringCount?: number;
  contractStatus?: DashboardContractStatus;
  injuredPlayers?: number;
  lastGame?: DashboardLastGame;
  maxPlayoffSeed?: number;
  maxPlayoffSeedNoPlayIn?: number;
  maxRosterSize?: number;
  messages: Message[];
  minRosterSize?: number;
  news?: DashboardNewsItem[];
  nextGame?: DashboardNextGame;
  pendingNegotiationCount?: number;
  unreadMessageCount?: number;
  // G1b: current-season owner trust (three factors + total + grace flag); only
  // sent for single-team, non-spectator leagues.
  ownerMood?: DashboardOwnerMood;
  phase?: number;
  phaseTask?: PhaseTask;
  riskAlerts?: DashboardRiskAlert[];
  // N4-0: this season's owner expectation letter (+ its mid), when one exists.
  seasonExpectation?: SeasonExpectation;
  teamSnapshot?: TeamSnapshot;
  // C4: pts/oppPts/trb/ast rank rows the view has always computed (rank 1 =
  // best, oppPts inverted server-side); optional because only some update
  // events refresh this section.
  teamStats?: DashboardStatRankLike[];
};

const visibleHomeFieldKeys = new Set(
  homeFields.filter((field) => field.tier !== "hidden").map((field) => field.key),
);

const DASHBOARD_MANDATORY_PHASES = new Set<number>([
  PHASE.EXPANSION_DRAFT,
  PHASE.FANTASY_DRAFT,
  PHASE.DRAFT_LOTTERY,
  PHASE.DRAFT,
  PHASE.RESIGN_PLAYERS,
  PHASE.FREE_AGENCY,
]);

const shouldShowHomeField = (key: string) => visibleHomeFieldKeys.has(key);

const EXPECTATION_PLAYOFF_DEFAULTS: Record<OwnerExpectations["playoffTarget"], string> = {
  contend: "Contend for the title",
  makePlayoffs: "Make the playoffs",
  develop: "Develop the roster",
};

const getDashboardGoalLabel = (
  goalState: DashboardSeasonGoalState,
  expectation: SeasonExpectation | undefined,
) => {
  const heuristicGoalLabel = i18n.t(`leagueDashboard.seasonGoal.${goalState.kind}`, {
    ns: "ui",
    defaultValue:
      goalState.kind === "playoffRun"
        ? "Win the playoff matchup"
        : goalState.kind === "securePlayoffs"
          ? "Secure playoff position"
          : goalState.kind === "chasePlayoffs"
            ? "Chase the playoff line"
            : "Develop the roster",
  });
  return expectation
    ? i18n.t(`leagueDashboard.expectationPlayoff.${expectation.playoffTarget}`, {
        ns: "ui",
        defaultValue: EXPECTATION_PLAYOFF_DEFAULTS[expectation.playoffTarget],
      })
    : heuristicGoalLabel;
};

// G1b / M5-1 / U2-2: owner trust is a compact status signal inside the season
// summary. The three factors remain available in one in-place MobileSheet; only
// the hot-seat tier keeps persistent warning copy on the page.
// Tier → emoji (language-neutral, no i18n needed) + short badge label. The emoji
// carries the tone at a glance; the label is a one-word verdict.
const OWNER_TRUST_TIER_EMOJI: Record<string, string> = {
  honeymoon: "🕊️",
  excellent: "🌟",
  good: "😀",
  solid: "🙂",
  shaky: "😟",
  hotSeat: "🔥",
};

const OWNER_TRUST_TIER_LABEL_DEFAULTS: Record<string, string> = {
  honeymoon: "Settling in",
  excellent: "Excellent",
  good: "Good",
  solid: "Steady",
  shaky: "Shaky",
  hotSeat: "Hot seat",
};

const OwnerTrustBlock = ({ ownerMood }: { ownerMood: DashboardOwnerMood }) => {
  const { tier, warning } = getDashboardOwnerTrustState(ownerMood);

  const tierLabel = i18n.t(`leagueDashboard.ownerTrustTierLabel.${tier}`, {
    ns: "ui",
    defaultValue: OWNER_TRUST_TIER_LABEL_DEFAULTS[tier],
  });
  return (
    <div
      className={`mt-2${warning ? " dashboard-owner-trust-warning" : ""}`}
      data-v5-dashboard-hot-seat={warning ? "" : undefined}
      data-v5-dashboard-owner-trust=""
      data-v5-dashboard-owner-trust-warning={warning ? "" : undefined}
    >
      <MobileStatusMetric
        label={i18n.t("leagueDashboard.ownerTrustTitle", {
          ns: "ui",
          defaultValue: "Owner trust",
        })}
        score={getOwnerTrustScore(tier)}
        value={
          <span data-v5-dashboard-owner-trust-tier={tier}>
            <span aria-hidden="true">{OWNER_TRUST_TIER_EMOJI[tier]}</span> {tierLabel}
          </span>
        }
      />
      {warning ? (
        <p className="mb-0 mt-1 small text-danger" data-v5-dashboard-owner-warning="">
          {i18n.t("leagueDashboard.ownerWarning", {
            ns: "ui",
            defaultValue: "Turn things around soon or your job is at risk.",
          })}
        </p>
      ) : null}
    </div>
  );
};

// Home briefing section 1: answer the three questions a new manager asks first
// without making them read a strategy memo — what happened, where do we stand,
// and are we above the qualification line? Preseason standings are deliberately
// shown as pending rather than presenting a meaningless rank from a 0-0 table.
const DashboardSeasonStatusSummary = ({
  expectation,
  gamesPlayed,
  goalState,
  lastGame,
  matchdayPrimary,
  maxPlayoffSeed,
  maxPlayoffSeedNoPlayIn,
  nextGame,
  nextPhaseTask,
  playoffRoundsWon,
  rank,
  record,
  roundsWonText,
  showMatchdayAction,
  userTid,
}: {
  expectation?: SeasonExpectation;
  gamesPlayed: number;
  goalState: DashboardSeasonGoalState;
  lastGame?: DashboardLastGame;
  matchdayPrimary: boolean;
  maxPlayoffSeed?: number;
  maxPlayoffSeedNoPlayIn?: number;
  nextGame?: DashboardNextGame;
  nextPhaseTask?: PhaseTask;
  playoffRoundsWon: number;
  rank: number;
  record: TeamSnapshot["record"];
  roundsWonText: string;
  showMatchdayAction: boolean;
  userTid: number;
}) => {
  const goalLabel = getDashboardGoalLabel(goalState, expectation);
  const riskLabel = i18n.t(`leagueDashboard.seasonGoalRisk.${goalState.riskLevel}`, {
    ns: "ui",
    defaultValue:
      goalState.riskLevel === "danger"
        ? "At risk"
        : goalState.riskLevel === "watch"
          ? "Watch"
          : "On track",
  });
  const progressText =
    gamesPlayed === 0
      ? i18n.t("leagueDashboard.seasonNotStarted", {
          ns: "ui",
          defaultValue: "The season has not started",
        })
      : expectation
        ? i18n.t("leagueDashboard.expectationProgress", {
            ns: "ui",
            defaultValue: "{{won}} / {{target}} wins · {{rank}}",
            rank: ordinalLocalized(rank),
            target: expectation.winTarget,
            won: record.won,
          })
        : playoffRoundsWon >= 0
          ? translateRoundsWonText(roundsWonText)
          : i18n.t("leagueDashboard.seasonGoalProgressRecord", {
              ns: "ui",
              defaultValue: "{{record}}, {{rank}}",
              rank: ordinalLocalized(rank),
              record: formatSeasonRecord(record),
            });
  const standingText =
    gamesPlayed === 0
      ? i18n.t("leagueDashboard.standingPending", {
          ns: "ui",
          defaultValue: "Pending",
        })
      : ordinalLocalized(rank);
  const qualificationText =
    gamesPlayed === 0
      ? i18n.t("leagueDashboard.qualificationNotStarted", {
          ns: "ui",
          defaultValue: "The qualification race begins on opening night",
        })
      : playoffRoundsWon >= 0
        ? i18n.t("leagueDashboard.qualificationPlayoffs", {
            ns: "ui",
            defaultValue: "Playoffs in progress",
          })
        : maxPlayoffSeedNoPlayIn !== undefined && rank <= maxPlayoffSeedNoPlayIn
          ? i18n.t("leagueDashboard.qualificationDirect", {
              ns: "ui",
              defaultValue: "Inside the direct playoff places",
            })
          : maxPlayoffSeed !== undefined && rank <= maxPlayoffSeed
            ? i18n.t("leagueDashboard.qualificationPlayIn", {
                ns: "ui",
                defaultValue: "Inside the play-in places",
              })
            : maxPlayoffSeed !== undefined
              ? i18n.t("leagueDashboard.qualificationOutside", {
                  ns: "ui",
                  count: Math.max(1, rank - maxPlayoffSeed),
                  defaultValue: "{{count}} place(s) below the qualification line",
                })
              : goalLabel;

  return (
    <MobileContentHero
      className="mb-3"
      data-dashboard-competitive-overview=""
      data-v5-dashboard-has-expectation={expectation ? "" : undefined}
      data-v5-dashboard-scoreboard-first-screen=""
      data-v5-dashboard-status-summary=""
    >
      <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
        <h2 className="h6 mb-0">
          {i18n.t("leagueDashboard.competitiveOverviewTitle", {
            ns: "ui",
            defaultValue: "Results and position",
          })}
        </h2>
        <a
          className="btn btn-link btn-sm p-0"
          data-dashboard-standings-link=""
          href={createMobileCrossTabHref(helpers.leagueUrl(["standings"]))}
        >
          {i18n.t("leagueDashboard.openStandings", {
            ns: "ui",
            defaultValue: "Standings",
          })}
        </a>
      </div>
      <div className="dashboard-summary-grid mb-3" data-dashboard-competitive-metrics="">
        <div className="dashboard-summary-item">
          <div className="small text-body-secondary">
            {i18n.t("leagueDashboard.record", { ns: "ui", defaultValue: "Record" })}
          </div>
          <strong data-dashboard-record-value="">
            {gamesPlayed === 0
              ? i18n.t("leagueDashboard.recordPending", {
                  ns: "ui",
                  defaultValue: "Not started",
                })
              : formatSeasonRecord(record)}
          </strong>
        </div>
        <div className="dashboard-summary-item">
          <div className="small text-body-secondary">
            {i18n.t("leagueDashboard.standing", { ns: "ui", defaultValue: "Standing" })}
          </div>
          <strong data-dashboard-standing-value="">{standingText}</strong>
        </div>
      </div>
      <div className="border-top pt-2" data-v5-dashboard-status-goal="">
        <MobileStatusMetric
          detail={goalLabel}
          label={
            expectation
              ? i18n.t("leagueDashboard.expectationCardTitle", {
                  ns: "ui",
                  defaultValue: "Owner's expectations",
                })
              : i18n.t("leagueDashboard.seasonGoalTitle", {
                  ns: "ui",
                  defaultValue: "Season goal",
                })
          }
          score={getSeasonGoalScore(goalState.riskLevel)}
          value={<span data-v5-dashboard-status-risk="">{riskLabel}</span>}
        />
        <div className="small text-body-secondary" data-dashboard-qualification-state="">
          {qualificationText}
        </div>
        <div className="small text-body-secondary" data-v5-dashboard-status-progress="">
          {progressText}
        </div>
      </div>
      <div className="d-flex align-items-center flex-wrap gap-2 mt-1">
        {expectation?.moneyTarget ? (
          <span className="badge text-bg-light border">
            {i18n.t("leagueDashboard.expectationMoney", {
              ns: "ui",
              defaultValue: "Stay profitable",
            })}
          </span>
        ) : null}
      </div>
      {nextPhaseTask ? (
        <div className="small text-body-secondary mt-2" data-v5-dashboard-status-next-node="">
          {i18n.t("leagueDashboard.statusNextNode", {
            ns: "ui",
            defaultValue: "After this: {{task}}",
            task: i18n.t(nextPhaseTask.titleKey, {
              ns: "ui",
              defaultValue: nextPhaseTask.defaultTitle,
            }),
          })}
        </div>
      ) : null}
      {nextGame ? (
        <div className="border-top mt-3 pt-2" data-dashboard-user-next-game="">
          <NextGameCard
            matchdayPrimary={matchdayPrimary}
            nextGame={nextGame}
            showMatchdayAction={showMatchdayAction}
            userTid={userTid}
          />
        </div>
      ) : null}
      {lastGame ? (
        <div className="border-top mt-3 pt-2">
          <LastGameCard lastGame={lastGame} userTid={userTid} />
        </div>
      ) : null}
    </MobileContentHero>
  );
};

// R1-2/R1-5: build a queue item's CTA href. Append the ?m_open=<sheetKey>
// intent so the destination page expands the target sheet on arrival, and wrap
// the whole thing in a cross-tab return seed — every dashboard task CTA crosses
// into another tab (roster / negotiation / finances / market root), so the
// global back control must be able to climb back to home once it is done.
const buildDashboardTaskHref = (action: { path: string[]; openSheet?: string }): string => {
  const base = helpers.leagueUrl(action.path);
  const withSheet = action.openSheet ? `${base}?m_open=${action.openSheet}` : base;
  return createMobileCrossTabHref(withSheet);
};

const DashboardTaskRowBody = ({ item }: { item: DashboardTaskItem }) => {
  const source = getDashboardTodoSource(item);

  return (
    <>
      <span>
        <span className={`fw-semibold${item.done ? " text-decoration-line-through" : ""}`}>
          {i18n.t(item.titleKey, {
            ns: "ui",
            defaultValue: item.defaultTitle,
          })}
        </span>
        {item.detailKey ? (
          <span className="d-block small text-body-secondary">
            {i18n.t(item.detailKey, {
              ns: "ui",
              defaultValue: item.defaultDetail,
              ...(item.detailParams ?? {}),
            })}
          </span>
        ) : null}
        <span
          className="d-block small text-body-secondary"
          data-v5-dashboard-todo-source={source.role}
        >
          {i18n.t(source.sourceKey, {
            ns: "ui",
            defaultValue: source.sourceDefault,
          })}
        </span>
      </span>
      {item.done ? (
        <span aria-hidden="true" className="text-success fw-bold align-self-center">
          ✓
        </span>
      ) : (
        <span
          className={`badge text-bg-${
            item.severity === "danger"
              ? "danger"
              : item.severity === "warning"
                ? "warning"
                : "secondary"
          } align-self-center`}
        >
          {i18n.t(`leagueDashboard.riskSeverity.${item.severity}`, {
            ns: "ui",
            defaultValue: item.severity,
          })}
        </span>
      )}
    </>
  );
};

// Home briefing section 2: only the next action and operational items that need
// attention belong here. Completed checks, capacity badges, owner mail, and
// agent advice have their own sections below, so a new player is not asked to
// decode a mixed queue with several different meanings.
const DashboardTaskQueue = ({
  mandatory,
  onOpenInboxDecision,
  overflowCount,
  primaryTaskSlot,
  todos,
}: Pick<DashboardTaskHub, "overflowCount" | "todos"> & {
  mandatory: boolean;
  onOpenInboxDecision: () => void;
  primaryTaskSlot: ReactNode;
}) => {
  if (!primaryTaskSlot && todos.length === 0) {
    return null;
  }

  return (
    <section
      className="mb-3"
      data-dashboard-required-actions={mandatory ? "mandatory" : "next"}
      data-v5-dashboard-task-queue=""
    >
      <h2 className="h6 mb-2">
        {i18n.t(mandatory ? "leagueDashboard.mustHandleTitle" : "leagueDashboard.nextActionTitle", {
          ns: "ui",
          defaultValue: mandatory ? "Must handle" : "Next step",
        })}
      </h2>
      {primaryTaskSlot}
      {todos.length > 0 ? (
        <div className="list-group list-group-flush">
          {todos.map((item) =>
            // G4-2: the inbox row is a "决定" entry — it opens the pending owner
            // letter in the centered OwnerLetterDialog (falling back to the inbox
            // list when nothing needs a decision), not the bottom-sheet list.
            item.sheet === "inbox" ? (
              <button
                className="list-group-item list-group-item-action d-flex justify-content-between gap-2 px-0"
                data-dashboard-inbox-task-entry=""
                data-v5-dashboard-task-item={item.key}
                key={item.key}
                onClick={onOpenInboxDecision}
                type="button"
              >
                <DashboardTaskRowBody item={item} />
              </button>
            ) : (
              <a
                className="list-group-item list-group-item-action d-flex justify-content-between gap-2 px-0"
                data-v5-dashboard-task-item={item.key}
                href={buildDashboardTaskHref(item)}
                key={item.key}
              >
                <DashboardTaskRowBody item={item} />
              </a>
            ),
          )}
        </div>
      ) : null}
      {overflowCount > 0 ? (
        <div className="small text-body-secondary mt-2">
          {i18n.t("leagueDashboard.moreBlockers", {
            ns: "ui",
            count: overflowCount,
            defaultValue: "+{{count}} more",
          })}
        </div>
      ) : null}
    </section>
  );
};

// C1/U2: the resolved dashboard home focus is one line + one CTA — no
// hero card, no eyebrow, no description paragraph, no impact narration (法则一).
// G2-2: the shared play sheet stashes the digest snapshot before a long advance.
const DashboardTaskLine = ({
  onOpenDecision,
  onLongAdvance,
  phase,
  playMenuOptions,
  primaryTask,
}: {
  onOpenDecision: () => void;
  onLongAdvance?: () => void;
  phase?: number;
  playMenuOptions: Option[];
  primaryTask: DashboardHomeFocus;
}) => {
  const primaryTaskAction =
    primaryTask.kind === "decision" ? "decision" : primaryTask.primaryAction.path.join("/");

  return (
    <div
      className="dashboard-task-line"
      data-v5-dashboard-decision-focus={primaryTask.kind === "decision" ? "" : undefined}
      data-v5-dashboard-phase-task-line=""
      data-v5-dashboard-primary-task-action={primaryTaskAction}
      data-v5-dashboard-primary-task-kind={primaryTask.kind}
      data-v5-dashboard-primary-task-phase={phase}
    >
      <span className="dashboard-task-line-title fw-semibold">
        {i18n.t(primaryTask.titleKey, {
          ns: "ui",
          defaultValue: primaryTask.defaultTitle,
        })}
      </span>
      <div className="dashboard-task-actions">
        {primaryTask.kind === "decision" ? (
          <button
            className={mobileActionButtonClassNames.primary}
            data-v5-dashboard-decision-focus-action=""
            data-v5-dashboard-primary-task-action-control={primaryTaskAction}
            onClick={onOpenDecision}
            type="button"
          >
            {i18n.t(primaryTask.primaryAction.labelKey, {
              ns: "ui",
              defaultValue: primaryTask.primaryAction.defaultLabel,
            })}
          </button>
        ) : (
          <>
            {/* R1-2/R1-5: non-dialog main tasks cross into another tab; append
              the ?m_open intent and seed the cross-tab return. */}
            <a
              className={mobileActionButtonClassNames.primary}
              data-v5-dashboard-primary-task-action-control={primaryTaskAction}
              href={buildDashboardTaskHref(primaryTask.primaryAction)}
            >
              {i18n.t(primaryTask.primaryAction.labelKey, {
                ns: "ui",
                defaultValue: primaryTask.primaryAction.defaultLabel,
              })}
            </a>
            <PlayOptionsMenu
              onLongAdvance={onLongAdvance}
              phase={phase}
              playMenuOptions={playMenuOptions}
            />
          </>
        )}
      </div>
    </div>
  );
};

// G2-3: the day-done stop point. When today's games are done and nothing
// dangerous is pending, the main task card becomes a "今天的事办完了" line with the
// next-game preview and a prominent 推进 CTA (reusing the shared play sheet's near
// group). Same slot as the task line so the page still has exactly one main card.
const DayDoneCard = ({
  nextGame,
  onLongAdvance,
  phase,
  playMenuOptions,
  userTid,
}: {
  nextGame: DashboardNextGame;
  onLongAdvance?: () => void;
  phase?: number;
  playMenuOptions: Option[];
  userTid: number;
}) => {
  const { teamInfoCache } = useLocalPartial(["teamInfoCache"]);
  const opponent = nextGame.teams.find((team) => team.tid !== userTid);
  const opponentInfo = opponent && opponent.tid >= 0 ? teamInfoCache[opponent.tid] : undefined;
  const opponentName = opponentInfo
    ? `${translateTeamRegion(opponentInfo.region)} ${translateTeamName(opponentInfo.name)}`
    : i18n.t("leagueDashboard.noOpponent", {
        ns: "ui",
        defaultValue: "Opponent TBD",
      });
  const whenLabel =
    nextGame.daysUntil === undefined
      ? undefined
      : i18n.t("leagueDashboard.matchdaysAway", {
          ns: "ui",
          defaultValue: "{{count}} matchdays away",
          count: nextGame.daysUntil,
        });

  return (
    <div className="dashboard-task-line" data-v5-dashboard-day-done="">
      <span className="dashboard-task-line-title">
        <span className="fw-semibold d-block">
          {i18n.t("leagueDashboard.dayDone.title", {
            ns: "ui",
            defaultValue: "That's it for today",
          })}
        </span>
        <span className="d-block small text-body-secondary" data-v5-dashboard-day-done-next="">
          {i18n.t("leagueDashboard.dayDone.nextUp", {
            ns: "ui",
            defaultValue: "Next up: {{opponent}}",
            opponent: opponentName,
          })}
          {whenLabel ? ` · ${whenLabel}` : ""}
        </span>
      </span>
      <div className="dashboard-task-actions">
        <PlayOptionsMenu
          onLongAdvance={onLongAdvance}
          phase={phase}
          playMenuOptions={playMenuOptions}
          triggerDefaultLabel="Move ahead"
          triggerLabelKey="leagueDashboard.dayDone.advanceCta"
          triggerVariant="primary"
        />
      </div>
    </div>
  );
};

// G2-2: the post-advance digest card ("这段时间"). Transient UI-only settlement —
// each row is a snapshot diff (advanceDigest.ts); rows with no change are
// omitted, and the whole card only renders when there is at least one row. The
// dismiss control clears the sessionStorage snapshot so a refresh does not
// resurrect it.
const DashboardAdvanceDigestCard = ({
  digest,
  lid,
  onDismiss,
}: {
  digest: AdvanceDigest;
  lid: number;
  onDismiss: () => void;
}) => {
  const rows: { key: string; text: string; won?: number; lost?: number }[] = [];
  if (digest.record) {
    rows.push({
      key: "record",
      lost: digest.record.lostDelta,
      text: i18n.t("leagueDashboard.advanceDigest.record", {
        ns: "ui",
        defaultValue: "Record +{{won}}W {{lost}}L",
        won: digest.record.wonDelta,
        lost: digest.record.lostDelta,
      }),
      won: digest.record.wonDelta,
    });
  }
  if (digest.injuredDelta !== undefined) {
    rows.push({
      key: "injuries",
      text:
        digest.injuredDelta > 0
          ? i18n.t("leagueDashboard.advanceDigest.injuriesUp", {
              ns: "ui",
              defaultValue: "{{count}} new injury(ies)",
              count: digest.injuredDelta,
            })
          : i18n.t("leagueDashboard.advanceDigest.injuriesDown", {
              ns: "ui",
              defaultValue: "{{count}} player(s) recovered",
              count: -digest.injuredDelta,
            }),
    });
  }
  if (digest.newLetters !== undefined) {
    rows.push({
      key: "letters",
      text: i18n.t("leagueDashboard.advanceDigest.letters", {
        ns: "ui",
        defaultValue: "{{count}} new letter(s)",
        count: digest.newLetters,
      }),
    });
  }
  if (digest.goalDelta !== undefined) {
    const goalDelta = Math.round(digest.goalDelta);
    rows.push({
      key: "goal",
      text:
        goalDelta >= 0
          ? i18n.t("leagueDashboard.advanceDigest.goalUp", {
              ns: "ui",
              defaultValue: "Season goal +{{count}}%",
              count: goalDelta,
            })
          : i18n.t("leagueDashboard.advanceDigest.goalDown", {
              ns: "ui",
              defaultValue: "Season goal -{{count}}%",
              count: -goalDelta,
            }),
    });
  }
  if (digest.ownerTrustDelta !== undefined) {
    rows.push({
      key: "owner",
      text: i18n.t(
        digest.ownerTrustDelta > 0
          ? "leagueDashboard.advanceDigest.ownerUp"
          : "leagueDashboard.advanceDigest.ownerDown",
        {
          ns: "ui",
          defaultValue:
            digest.ownerTrustDelta > 0
              ? "The owner's evaluation improved"
              : "The owner's evaluation declined",
        },
      ),
    });
  }

  return (
    <MobileContentHero
      className="mb-3"
      data-v5-dashboard-advance-digest=""
      data-v5-dashboard-advance-digest-lid={lid}
    >
      <div className="d-flex align-items-start justify-content-between gap-3">
        <div className="text-body-secondary small text-uppercase">
          {i18n.t("leagueDashboard.advanceDigest.title", {
            ns: "ui",
            defaultValue: "While you were away",
          })}
        </div>
        <button
          aria-label={i18n.t("leagueDashboard.advanceDigest.dismiss", {
            ns: "ui",
            defaultValue: "Dismiss",
          })}
          className="btn-close"
          data-v5-dashboard-advance-digest-dismiss=""
          onClick={onDismiss}
          type="button"
        />
      </div>
      <div className="list-group list-group-flush">
        {rows.map((row) => (
          <div
            className="list-group-item px-0 py-1 border-0 bg-transparent"
            data-v5-dashboard-advance-digest-row={row.key}
            data-v5-dashboard-advance-digest-record-lost={row.lost}
            data-v5-dashboard-advance-digest-record-won={row.won}
            key={row.key}
          >
            {row.text}
          </div>
        ))}
      </div>
    </MobileContentHero>
  );
};

// G7-1: a one-time "new achievement" entry surfaced on the home surface after a
// celebration moment (championship / milestone) is closed with fresh unlocks this
// session. It opens the AchievementsSheet (the same trophy case reached from the
// history hub) and is consumed on view/dismiss — a transient prompt, no back stack.
const DashboardNewAchievementsCard = ({
  count,
  onView,
  onDismiss,
}: {
  count: number;
  onView: () => void;
  onDismiss: () => void;
}) => (
  <MobileContentHero className="mb-3" data-v5-dashboard-new-achievements="">
    <div className="d-flex align-items-start justify-content-between gap-3">
      <div>
        <div className="text-body-secondary small text-uppercase">
          {i18n.t("leagueDashboard.newAchievements.eyebrow", {
            ns: "ui",
            defaultValue: "New achievement",
          })}
        </div>
        <div className="fw-semibold" data-v5-dashboard-new-achievements-count="">
          {i18n.t("leagueDashboard.newAchievements.title", {
            ns: "ui",
            count,
            defaultValue: "{{count}} new achievement(s) unlocked",
          })}
        </div>
      </div>
      <button
        aria-label={i18n.t("leagueDashboard.newAchievements.dismiss", {
          ns: "ui",
          defaultValue: "Dismiss",
        })}
        className="btn-close"
        data-v5-dashboard-new-achievements-dismiss=""
        onClick={onDismiss}
        type="button"
      />
    </div>
    <button
      className="btn btn-outline-secondary btn-sm mt-2"
      data-v5-dashboard-new-achievements-view=""
      onClick={onView}
      type="button"
    >
      {i18n.t("leagueDashboard.newAchievements.view", {
        ns: "ui",
        defaultValue: "View achievements",
      })}
    </button>
  </MobileContentHero>
);

// C4/U2-2 data-story conclusions are secondary evidence after the task queue
// (the old "More context" fold that held them is gone): the view's
// pts/oppPts/trb/ast rank rows (previously unrendered) become at most two
// top-rank conclusions; tapping one expands the full four-metric rank table
// as evidence. statInsights gates (games ≥ 5, rank ≤ 5, value > 0) decide
// rendering — no conclusion, no block.
const dashboardInsightStatLabelDefaults: Record<string, string> = {
  ast: "Assists per game",
  oppPts: "Points allowed per game",
  pts: "Points scored per game",
  trb: "Rebounds per game",
};

const getDashboardInsightStatLabel = (stat: string) =>
  i18n.t(`statInsights.statLabel.${stat}`, {
    ns: "ui",
    defaultValue: dashboardInsightStatLabelDefaults[stat] ?? stat,
  });

const DashboardStatInsightsBlock = ({
  gamesPlayed,
  teamStats,
}: {
  gamesPlayed: number;
  teamStats: DashboardStatRankLike[] | undefined;
}) => {
  const insights = getDashboardStatRankInsights(teamStats, { gamesPlayed });

  if (insights.length === 0) {
    return null;
  }

  const evidence = (teamStats ?? [])
    .filter(
      (entry): entry is { rank: number; stat: string; value: number } =>
        typeof entry.rank === "number" && entry.rank >= 1 && typeof entry.value === "number",
    )
    .map((entry) => (
      <MobileStatRow
        highlighted
        key={entry.stat}
        label={getDashboardInsightStatLabel(entry.stat)}
        rank={`#${entry.rank}`}
        value={helpers.roundStat(entry.value, entry.stat)}
      />
    ));

  return (
    <MobileStatInsights
      className="mb-3"
      data-dashboard-stat-insights=""
      items={insights.map((insight) => ({
        conclusion: `${getDashboardInsightStatLabel(insight.stat)} ${helpers.roundStat(
          insight.value,
          insight.stat,
        )} · ${i18n.t("statInsights.leagueRank", {
          ns: "ui",
          defaultValue: "league #{{rank}}",
          rank: insight.rank,
        })}`,
        evidence,
        id: `dashboard-${insight.stat}`,
        tone: "positive" as const,
      }))}
    />
  );
};

// C1: last completed game as a score card — both logos, the big score, one
// result line (win/loss word + key player). The whole card opens the box score.
const LastGameCard = ({
  lastGame,
  userTid,
}: {
  lastGame: DashboardLastGame | undefined;
  userTid: number;
}) => {
  const { teamInfoCache } = useLocalPartial(["teamInfoCache"]);

  if (!lastGame) {
    return null;
  }

  const userIndex = lastGame.teams.findIndex((t) => t.tid === userTid);

  if (userIndex === -1) {
    return null;
  }

  const winner = getWinner(lastGame.teams);
  const resultKind = winner === -1 ? "tie" : winner === userIndex ? "win" : "loss";
  const resultLabel = i18n.t(`leagueDashboard.lastGameResult.${resultKind}`, {
    ns: "ui",
    defaultValue: resultKind === "win" ? "Win" : resultKind === "loss" ? "Loss" : "Tie",
  });
  const overtimeText = formatOvertimeSummary(lastGame.overtimes);
  const userAbbrev = teamInfoCache[userTid]?.abbrev ?? "";
  // N2-1: box score lives in the games tab; seed a return so the back control
  // climbs back to the home dashboard precisely (look-up cross-tab CTA).
  const boxScoreHref = createMobileCrossTabHref(
    helpers.leagueUrl(["game_log", `${userAbbrev}_${userTid}`, lastGame.season, lastGame.gid]),
  );
  const keyPlayer = lastGame.keyPlayer;
  const keyPlayerStatText = keyPlayer?.stats
    .map(({ stat, value }) => `${value} ${getCol(`stat:${stat}`).title}`)
    .join(" / ");

  // G3-1: "your decision" credit for a traded-for / signed new arrival who
  // suited up. Worker-derived; single line (best performer), never a stack.
  const attribution = lastGame.attribution;
  const attributionVariant = attribution ? getGameAttributionVariant(attribution) : undefined;
  const attributionText =
    attribution && attributionVariant
      ? i18n.t(`leagueDashboard.attribution.${attributionVariant}`, {
          ns: "ui",
          defaultValue: GAME_ATTRIBUTION_DEFAULTS[attributionVariant],
          name: attribution.name,
          stats: attribution.stats
            .map(({ stat, value }) => `${value} ${getCol(`stat:${stat}`).title}`)
            .join(" / "),
        })
      : undefined;

  return (
    <a
      aria-label={i18n.t("leagueDashboard.openBoxScore", {
        ns: "ui",
        defaultValue: "Open box score",
      })}
      className="dashboard-last-game-card"
      data-v5-dashboard-last-game=""
      href={boxScoreHref}
    >
      <div className="text-body-secondary small">
        {i18n.t("leagueDashboard.lastGame", {
          ns: "ui",
          defaultValue: "Last game",
        })}
      </div>
      {/* Away team first, then home, matching the shared score box order. */}
      {[1, 0].map((i) => {
        const t = lastGame.teams[i]!;
        const info = t.tid >= 0 ? teamInfoCache[t.tid] : undefined;
        const won = winner === i;

        return (
          <div className="dashboard-last-game-row" key={i}>
            <TeamLogoInline
              imgURL={info?.imgURLSmall ?? info?.imgURL}
              includePlaceholderIfNoLogo
              size={36}
            />
            <span
              className={`dashboard-last-game-team text-truncate${
                t.tid === userTid ? " fw-semibold" : ""
              }`}
            >
              {info ? translateTeamName(info.name) : "-"}
            </span>
            <span
              className={`dashboard-last-game-score${won ? " fw-bold" : " text-body-secondary"}`}
            >
              <TeamScoreValue pts={t.pts} sPts={t.sPts} shootoutClassName="fw-normal small" />
            </span>
          </div>
        );
      })}
      <div className="dashboard-last-game-result small" data-v5-dashboard-last-game-result="">
        <span
          className={`badge ${
            resultKind === "win"
              ? "text-bg-success"
              : resultKind === "loss"
                ? "text-bg-danger"
                : "text-bg-warning"
          }`}
        >
          {resultLabel}
        </span>
        {overtimeText ? <span className="text-body-secondary">{overtimeText}</span> : null}
        {keyPlayer ? (
          <span className="text-body-secondary text-truncate" data-v5-dashboard-key-player="">
            {keyPlayer.name}
            {keyPlayerStatText ? ` · ${keyPlayerStatText}` : ""}
          </span>
        ) : null}
      </div>
      {attributionText ? (
        <div
          className="dashboard-last-game-attribution small fw-semibold"
          data-v5-dashboard-attribution={attributionVariant}
        >
          {attributionText}
        </div>
      ) : null}
    </a>
  );
};

// C1: next game as a matchup card — opponent logo, home/away, opponent record,
// and the matchday CTA. When the phase task points at a matchday due now, this
// CTA IS the page's single primary action (法则二); future day-done state hides it.
const NextGameCard = ({
  matchdayPrimary,
  nextGame,
  showMatchdayAction,
  userTid,
}: {
  matchdayPrimary: boolean;
  nextGame: DashboardNextGame | undefined;
  showMatchdayAction: boolean;
  userTid: number;
}) => {
  const { teamInfoCache } = useLocalPartial(["teamInfoCache"]);

  if (!shouldShowHomeField("nextGame") || !nextGame) {
    return null;
  }

  const userIsHome = nextGame.teams[0].tid === userTid;
  const opponent = nextGame.teams.find((team) => team.tid !== userTid);
  const opponentInfo = opponent && opponent.tid >= 0 ? teamInfoCache[opponent.tid] : undefined;
  const opponentName = opponentInfo
    ? `${translateTeamRegion(opponentInfo.region)} ${translateTeamName(opponentInfo.name)}`
    : i18n.t("leagueDashboard.noOpponent", {
        ns: "ui",
        defaultValue: "Opponent TBD",
      });
  const opponentRecord =
    opponent && opponent.won !== undefined && opponent.lost !== undefined
      ? formatSeasonRecord({ lost: opponent.lost, won: opponent.won })
      : undefined;

  // D5 (C1 leftover): the schedule model has day ordinals, not clock times.
  // Say when the game happens in matchday terms instead of showing nothing.
  const whenLabel =
    nextGame.daysUntil === undefined
      ? undefined
      : nextGame.daysUntil <= 0
        ? i18n.t("leagueDashboard.nextMatchdayNow", {
            ns: "ui",
            defaultValue: "Next matchday",
          })
        : i18n.t("leagueDashboard.matchdaysAway", {
            ns: "ui",
            defaultValue: "{{count}} matchdays away",
            count: nextGame.daysUntil,
          });

  return (
    <div className="dashboard-next-game-card" data-v5-dashboard-next-game="">
      <div className="text-body-secondary small">
        {i18n.t("leagueDashboard.nextGame", {
          ns: "ui",
          defaultValue: "Next game",
        })}
      </div>
      <div className="dashboard-matchup-row" data-u9-dashboard-matchup-layout="">
        <TeamLogoInline
          imgURL={opponentInfo?.imgURLSmall ?? opponentInfo?.imgURL}
          includePlaceholderIfNoLogo
          size={36}
        />
        <div className="dashboard-matchup-team" data-u9-dashboard-matchup-team="">
          <span className="d-block fw-semibold text-truncate" data-u9-dashboard-opponent-name="">
            {opponentName}
          </span>
          <span className="d-block small text-body-secondary" data-u9-dashboard-matchup-meta="">
            {userIsHome
              ? i18n.t("leagueDashboard.homeGame", {
                  ns: "ui",
                  defaultValue: "Home",
                })
              : i18n.t("leagueDashboard.awayGame", {
                  ns: "ui",
                  defaultValue: "Away",
                })}
            {opponentRecord ? ` · ${opponentRecord}` : ""}
            {whenLabel ? <span data-d5-dashboard-next-game-when=""> · {whenLabel}</span> : null}
          </span>
        </div>
        {showMatchdayAction ? (
          <a
            className={
              matchdayPrimary ? mobileActionButtonClassNames.primary : "btn btn-outline-primary"
            }
            data-v5-dashboard-matchday-cta=""
            href={createMobileCrossTabHref(helpers.leagueUrl(["daily_schedule"]))}
          >
            {i18n.t("leagueDashboard.enterMatchday", {
              ns: "ui",
              defaultValue: "Open matchday",
            })}
          </a>
        ) : null}
      </div>
    </div>
  );
};

const DashboardTeamState = ({
  injuredPlayers,
  maxRosterSize,
  minRosterSize,
  snapshot,
}: {
  injuredPlayers: number;
  maxRosterSize?: number;
  minRosterSize?: number;
  snapshot: TeamSnapshot;
}) => {
  const strength = getTeamStrengthStatus(snapshot.teamOvr);
  const roster = getRosterStatus({
    count: snapshot.rosterCount,
    max: maxRosterSize,
    min: minRosterSize,
  });
  const capSpace = getCapSpaceStatus({
    capSpace: snapshot.capSpace,
    payrollLimit: snapshot.payrollLimit,
  });
  const unavailable = i18n.t("common.noData", { ns: "ui", defaultValue: "No data yet." });
  const strengthText = strength.state
    ? i18n.t(`leagueDashboard.strengthState.${strength.state}`, {
        ns: "ui",
        defaultValue:
          strength.state === "unformed"
            ? "Roster not formed"
            : strength.state === "urgent"
              ? "Urgent reinforcements needed"
              : strength.state === "formed"
                ? "Roster taking shape"
                : strength.state === "solid"
                  ? "Solid roster"
                  : "Strong roster",
      })
    : unavailable;
  const rosterText =
    roster.state === "short"
      ? i18n.t("leagueDashboard.rosterNeedsPlayers", {
          ns: "ui",
          count: roster.missing,
          defaultValue: "Need {{count}} more player(s)",
        })
      : roster.state === "full"
        ? i18n.t("leagueDashboard.rosterFullStatus", {
            ns: "ui",
            defaultValue: "Roster is full",
          })
        : roster.state === "complete"
          ? i18n.t("leagueDashboard.rosterCompleteStatus", {
              ns: "ui",
              defaultValue: "Roster complete",
            })
          : unavailable;
  const capSpaceText =
    capSpace.state === "over"
      ? i18n.t("leagueDashboard.capOverStatus", {
          ns: "ui",
          amount: helpers.formatCurrency(Math.abs(snapshot.capSpace!), "M"),
          defaultValue: "{{amount}} over the limit",
        })
      : capSpace.state === "near"
        ? i18n.t("leagueDashboard.capNearStatus", {
            ns: "ui",
            defaultValue: "Near the limit",
          })
        : capSpace.state === "available"
          ? i18n.t("leagueDashboard.capAvailableStatus", {
              ns: "ui",
              amount: helpers.formatCurrency(snapshot.capSpace!, "M"),
              defaultValue: "{{amount}} available",
            })
          : unavailable;

  return (
    <section className="mb-3" data-dashboard-team-state="">
      <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
        <h2 className="h6 mb-0">
          {i18n.t("leagueDashboard.teamStateTitle", {
            ns: "ui",
            defaultValue: "Team status",
          })}
        </h2>
        <a
          className="btn btn-link btn-sm p-0"
          href={createMobileCrossTabHref(helpers.leagueUrl(["roster"]))}
        >
          {i18n.t("leagueDashboard.openRoster", {
            ns: "ui",
            defaultValue: "Roster",
          })}
        </a>
      </div>
      <div className="dashboard-summary-grid">
        <MobileStatusMetric
          className="dashboard-summary-item"
          data-dashboard-team-state-metric="strength"
          label={i18n.t("leagueDashboard.teamStrength", {
            ns: "ui",
            defaultValue: "Team strength",
          })}
          score={strength.score}
          value={strengthText}
        />
        <MobileStatusMetric
          className="dashboard-summary-item"
          data-dashboard-team-state-metric="roster"
          label={i18n.t("leagueDashboard.rosterCount", {
            ns: "ui",
            defaultValue: "Roster",
          })}
          score={roster.score}
          value={rosterText}
        />
        <MobileStatusMetric
          className="dashboard-summary-item"
          data-dashboard-team-state-metric="injuries"
          label={i18n.t("leagueDashboard.injuryStatus", {
            ns: "ui",
            defaultValue: "Injuries",
          })}
          score={getHealthScore(injuredPlayers)}
          value={
            injuredPlayers === 0
              ? i18n.t("leagueDashboard.allHealthyStatus", {
                  ns: "ui",
                  defaultValue: "All healthy",
                })
              : i18n.t("leagueDashboard.injuredCountStatus", {
                  ns: "ui",
                  count: injuredPlayers,
                  defaultValue: "{{count}} injured",
                })
          }
        />
        <MobileStatusMetric
          className="dashboard-summary-item"
          data-dashboard-team-state-metric="cap-space"
          label={i18n.t("leagueDashboard.availableSpace", {
            ns: "ui",
            defaultValue: "Available space",
          })}
          score={capSpace.score}
          value={capSpaceText}
        />
      </div>
    </section>
  );
};

const DashboardAgentAdvice = ({
  contractStatus,
  salaryPressure,
}: {
  contractStatus: DashboardContractStatus;
  salaryPressure: boolean;
}) => {
  const hasAdvice = contractStatus.kind !== "clear" || salaryPressure;

  return (
    <section className="mb-3" data-dashboard-agent-advice="">
      <h2 className="h6 mb-2">
        {i18n.t("leagueDashboard.agentAdviceTitle", {
          ns: "ui",
          defaultValue: "Agent advice",
        })}
      </h2>
      <MobileContentHero>
        {hasAdvice ? (
          <div className="list-group list-group-flush">
            {contractStatus.kind === "actionable" ? (
              <a
                className="list-group-item list-group-item-action border-0 p-0 mb-2 text-decoration-none"
                data-dashboard-agent-advice-item="contracts"
                href={buildDashboardTaskHref({ path: ["negotiation"] })}
              >
                <MobileStatusMetric
                  className="w-100"
                  detail={i18n.t(
                    "leagueDashboard.agentDeadlineNow",
                    {
                      ns: "ui",
                      defaultValue: "Due now",
                    },
                  )}
                  label={i18n.t("leagueDashboard.riskContracts", {
                    ns: "ui",
                    defaultValue: "Contracts expiring",
                  })}
                  score={getContractStatusScore({
                    count: contractStatus.count,
                    urgent: true,
                  })}
                  value={i18n.t("leagueDashboard.riskContractsDetail", {
                    ns: "ui",
                    count: contractStatus.count,
                    defaultValue: "{{count}} player(s) need a decision",
                  })}
                />
              </a>
            ) : null}
            {contractStatus.kind === "planning" ? (
              <div className="mb-2" data-dashboard-agent-advice-item="contract-planning">
                <MobileStatusMetric
                  className="w-100"
                  detail={i18n.t("leagueDashboard.agentContractPlanningLater", {
                    ns: "ui",
                    defaultValue: "Handled during the re-signing phase",
                  })}
                  label={i18n.t("leagueDashboard.agentContractPlanning", {
                    ns: "ui",
                    defaultValue: "Contracts ending this season",
                  })}
                  score={72}
                  value={i18n.t("leagueDashboard.agentContractPlanningDetail", {
                    ns: "ui",
                    count: contractStatus.count,
                    defaultValue: "{{count}} contract(s) end after the season",
                  })}
                />
              </div>
            ) : null}
            {salaryPressure ? (
              <a
                className="list-group-item list-group-item-action border-0 p-0 text-decoration-none"
                data-dashboard-agent-advice-item="salary"
                href={buildDashboardTaskHref({ path: ["team_finances"] })}
              >
                <MobileStatusMetric
                  className="w-100"
                  label={i18n.t("leagueDashboard.riskSalary", {
                    ns: "ui",
                    defaultValue: "Salary pressure",
                  })}
                  score={16}
                  value={i18n.t("leagueDashboard.riskSalaryDetail", {
                    ns: "ui",
                    defaultValue: "Payroll is over the current limit",
                  })}
                />
              </a>
            ) : null}
          </div>
        ) : (
          <div data-dashboard-agent-advice-empty="">
            <MobileStatusMetric
              label={i18n.t("leagueDashboard.agentAdviceTitle", {
                ns: "ui",
                defaultValue: "Agent advice",
              })}
              score={100}
              value={i18n.t("leagueDashboard.agentAdviceEmpty", {
                ns: "ui",
                defaultValue: "No contract or salary decision needs your attention.",
              })}
            />
          </div>
        )}
        {hasAdvice ? (
          <p className="small text-body-secondary mb-0 mt-2" data-dashboard-agent-automation-note="">
            {contractStatus.kind === "planning"
              ? i18n.t("leagueDashboard.agentContractPlanningNote", {
                  ns: "ui",
                  defaultValue:
                    "No action is needed yet. These decisions open in the re-signing phase.",
                })
              : contractStatus.kind === "actionable"
                ? i18n.t("leagueDashboard.agentAutomationNote", {
                    ns: "ui",
                    defaultValue:
                      "Agents do not make these decisions for you. An unsigned expiring player becomes a free agent, and salary pressure does not resolve automatically.",
                  })
                : i18n.t("leagueDashboard.agentSalaryAutomationNote", {
                    ns: "ui",
                    defaultValue: "Salary pressure does not resolve automatically.",
                  })}
          </p>
        ) : null}
      </MobileContentHero>
    </section>
  );
};

const DashboardInformation = ({
  onOpen,
  ownerMood,
  unreadCount,
}: {
  onOpen: () => void;
  ownerMood?: DashboardOwnerMood;
  unreadCount: number;
}) => {
  const ownerWarning = ownerMood ? getDashboardOwnerTrustState(ownerMood).warning : false;

  return (
    <section className="mb-3" data-dashboard-information-center="">
      <h2 className="h6 mb-2">
        {i18n.t("leagueDashboard.informationTitle", {
          ns: "ui",
          defaultValue: "Information",
        })}
      </h2>
      <MobileContentHero>
        {ownerWarning && ownerMood ? (
          <div className="mb-3" data-dashboard-information-owner-warning="">
            <OwnerTrustBlock ownerMood={ownerMood} />
          </div>
        ) : null}
        <button
          aria-label={i18n.t("leagueDashboard.informationOpenUnread", {
            ns: "ui",
            count: unreadCount,
            defaultValue: "Open {{count}} unread messages",
          })}
          className="dashboard-unread-messages-button"
          data-dashboard-inbox-history-entry=""
          data-dashboard-information-open=""
          onClick={onOpen}
          type="button"
        >
          <MobileStatRow
            data-dashboard-information-unread=""
            data-information-center-unread=""
            label={i18n.t("leagueDashboard.informationUnread", {
              ns: "ui",
              defaultValue: "Unread messages",
            })}
            value={i18n.t("leagueDashboard.unreadMessages", {
              ns: "ui",
              count: unreadCount,
              defaultValue: "{{count}} unread",
            })}
          />
        </button>
      </MobileContentHero>
    </section>
  );
};

const LeagueDashboard = ({
  contractExpiringCount,
  contractStatus,
  injuredPlayers,
  lastGame,
  lost,
  maxPlayoffSeed,
  maxPlayoffSeedNoPlayIn,
  maxRosterSize,
  messages,
  minRosterSize,
  name,
  news,
  otl,
  ownerMood,
  pendingNegotiationCount,
  phase,
  phaseTask,
  playoffRoundsWon,
  rank,
  region,
  riskAlerts,
  roundsWonText,
  season,
  seasonExpectation,
  teamSnapshot,
  teamStats,
  tied,
  unreadMessageCount,
  userTid,
  won,
  nextGame,
}: LeagueDashboardProps) => {
  useTitleBar({
    title: `${translateTeamRegion(region)} ${translateTeamName(name)} ${i18n.t("menu.Dashboard", { ns: "ui", defaultValue: "Dashboard" })}`,
  });
  const { lid, mobileTabAlerts, playMenuOptions } = useLocalPartial([
    "lid",
    "mobileTabAlerts",
    "playMenuOptions",
  ]);

  // M3-5: the retired inbox + message pages are now the two-level InboxSheet,
  // hosted here on the home surface (the "工具" archive container). openInboxSheet(mid?)
  // opens it — no mid lands on the list level, a mid drills straight to that letter
  // (?m_open_mid archive semantics, unchanged).
  const [inboxOpen, setInboxOpen] = useState(false);
  const [inboxMid, setInboxMid] = useState<number | undefined>(undefined);
  const [locallyReadMessageIds, setLocallyReadMessageIds] = useState<Set<number>>(() => new Set());
  useEffect(() => {
    // Once the worker publishes a fresh authoritative total, discard local
    // optimistic decrements so the same read is never subtracted twice.
    setLocallyReadMessageIds(new Set());
  }, [unreadMessageCount]);
  const markInformationMessageRead = useCallback((mid: number) => {
    setLocallyReadMessageIds((previous) => {
      if (previous.has(mid)) {
        return previous;
      }
      const next = new Set(previous);
      next.add(mid);
      return next;
    });
  }, []);
  const unreadCount = Math.max(
    0,
    (unreadMessageCount ?? messages.filter((message) => !message.read).length) -
      locallyReadMessageIds.size,
  );
  const openInboxSheet = (mid?: number) => {
    setInboxMid(mid);
    setInboxOpen(true);
  };

  // G4-2: a pending owner letter is a "决定", shown in the centered OwnerLetterDialog
  // rather than the inbox list. The dashboard's message summary only carries
  // {mid, read, year, from}, so "the latest pending decision" is resolved UI-side
  // from getInboxSheet (which ships subject/text the classifier needs).
  const [letterDialogOpen, setLetterDialogOpen] = useState(false);
  const [letterDialogMid, setLetterDialogMid] = useState<number | undefined>(undefined);

  // Resolve the newest unread *decision* letter (classifier "task"). getInboxSheet
  // returns newest-first, so the first task is the latest pending decision.
  const resolveLatestPendingDecisionMid = async (): Promise<number | undefined> => {
    const result = await toWorker("main", "getInboxSheet", undefined);
    const pending = result.messages.find((message) => classifyInboxMessage(message) === "task");
    return pending?.mid;
  };

  // G4-2: the home inbox decision entry (and the ?m_open_latest deep link) route to
  // the decision dialog when an unread decision letter exists; otherwise they fall
  // back to the InboxSheet list (the archive tool).
  const openInboxDecision = () => {
    void (async () => {
      const mid = await resolveLatestPendingDecisionMid();
      if (mid !== undefined) {
        setLetterDialogMid(mid);
        setLetterDialogOpen(true);
      } else {
        openInboxSheet();
      }
    })();
  };

  // Deep-link landing: worker/UI producers that used to point at /message now land
  // here with a one-shot query param. ?m_open_latest=1 (the play menu's "Read new
  // message") opens the latest pending decision in the OwnerLetterDialog, falling
  // back to the inbox list; ?m_open_mid=<mid> keeps the archive semantics (the
  // InboxSheet two-level browse). React to the settled route URL rather than mount
  // only: the SPA preserves this component when only the query changes. Consume the
  // intent, then strip it via history.replaceState so refresh/back does not reopen
  // the overlay — HOST landing logic, not the sheet/dialog touching the back stack.
  const dashboardRouteIntentUrl =
    typeof window === "undefined"
      ? undefined
      : `${window.location.pathname}${window.location.search}${window.location.hash}`;
  useEffect(() => {
    if (dashboardRouteIntentUrl === undefined) {
      return;
    }

    const { cleanUrl, consumed, intent } = getDashboardRouteIntent(dashboardRouteIntentUrl);
    if (!consumed) {
      return;
    }

    const latest = intent?.kind === "latest";
    if (latest) {
      openInboxDecision();
    } else if (intent?.kind === "message") {
      setInboxMid(intent.mid);
      setInboxOpen(true);
    }

    window.history.replaceState(window.history.state, "", cleanUrl);
    // openInboxDecision is stable enough for this one-shot route intent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardRouteIntentUrl]);

  const snapshot: TeamSnapshot = teamSnapshot ?? {
    rank,
    record: { lost, otl, tied, won },
  };
  const resolvedContractStatus =
    contractStatus ??
    getDashboardContractStatus({
      contractExpiringCount,
      pendingNegotiationCount,
      phase,
    });
  const riskAlertsForTaskHub = riskAlerts ?? [];

  // N4-0 ③: per-phase checklist derived from real team state — one of the three
  // inputs the G1-1 merged queue consolidates (with riskAlerts and the inbox).
  const phaseChecklist =
    phase !== undefined && minRosterSize !== undefined && maxRosterSize !== undefined
      ? getDashboardPhaseChecklist({
          phase,
          rosterCount: snapshot.rosterCount,
          minRosterSize,
          maxRosterSize,
          pendingNegotiationCount:
            resolvedContractStatus.kind === "actionable" ? resolvedContractStatus.count : 0,
          injuredPlayers,
        })
      : [];

  // G1-2: the season-goal card becomes a driver — when a playoff goal is
  // slipping (watch/danger) it injects one goal-driven suggestion into the
  // queue that points at the phase-aware market root.
  const goalState = getDashboardSeasonGoalState({
    expectation: seasonExpectation,
    playoffRoundsWon,
    rank,
    record: snapshot.record,
    rosterCount: snapshot.rosterCount,
  });
  const goalAdvice = getDashboardGoalAdvice(goalState, phase);

  // G1-1: one merged, deduped, priority-ordered queue for the whole home
  // surface — the old「本阶段」checklist, the「待办」risk list and the letters
  // rows all collapse in here.
  const taskHub: DashboardTaskHub = getDashboardTaskHub({
    checklistItems: phaseChecklist,
    goalAdvice,
    hasInboxDecision: mobileTabAlerts.home,
    messages,
    phase,
    phaseTask,
    riskAlerts: riskAlertsForTaskHub,
    unreadMessageCount: unreadCount,
  });
  const fallbackPrimaryTask = getDashboardFallbackPrimaryTask();
  const primaryTask = taskHub.primaryTask ?? fallbackPrimaryTask;
  const operationalTodos = taskHub.todos.filter(
    (item) => item.source === "coaching" && item.concept !== "goalAdvice",
  );
  const primaryTaskMandatory =
    primaryTask.kind === "decision" ||
    primaryTask.kind === "blocker" ||
    (phase !== undefined && DASHBOARD_MANDATORY_PHASES.has(phase));
  const actionSectionMandatory =
    primaryTaskMandatory ||
    operationalTodos.some((item) => item.severity === "danger" || item.concept === "injuries");

  // G2-2: post-advance digest. A long advance triggered from the home play sheet
  // stashes this pre-advance snapshot; when the dashboard re-renders with the
  // post-sim data we diff against it and render a dismissible "这段时间" card. The
  // snapshot is read from the current lid's session key (surviving a refresh
  // mid-advance) and updated in place by the long-advance click. Changing leagues
  // rehydrates that league's baseline and cannot compare against stale React
  // state from the previous league; dismissing clears only the current lid.
  const [advanceSnapshot, setAdvanceSnapshot] = useState(() =>
    lid === undefined ? undefined : readAdvanceSnapshot(lid),
  );
  useEffect(() => {
    setAdvanceSnapshot(lid === undefined ? undefined : readAdvanceSnapshot(lid));
  }, [lid]);
  const currentAdvanceState =
    lid === undefined
      ? undefined
      : {
          lid,
          season,
          won,
          lost,
          injured: injuredPlayers ?? 0,
          unread: unreadCount,
          goalProgress: goalState.progressPercent,
          ownerTrust: ownerMood?.total,
        };
  const recordAdvanceSnapshot = () => {
    if (!currentAdvanceState) {
      return;
    }
    writeAdvanceSnapshot(currentAdvanceState);
    setAdvanceSnapshot(currentAdvanceState);
  };
  const dismissAdvanceDigest = () => {
    if (lid !== undefined) {
      clearAdvanceSnapshot(lid);
    }
    setAdvanceSnapshot(undefined);
  };
  const advanceDigest =
    advanceSnapshot && currentAdvanceState && advanceSnapshot.lid === lid
      ? computeAdvanceDigest(advanceSnapshot, currentAdvanceState)
      : undefined;

  // G7-1: the one-time "new achievement" home prompt. `readAchievementPrompt`
  // returns a count only when a celebration was closed this session AND at least
  // one achievement was unlocked. The emitter re-reads it when a celebration closes
  // while this view is already mounted (the common championship-from-home flow).
  const [achievementPromptCount, setAchievementPromptCount] = useState(() =>
    readAchievementPrompt(),
  );
  const [achievementsSheetOpen, setAchievementsSheetOpen] = useState(false);
  useEffect(() => {
    const unbind = achievementPromptEmitter.on("change", () => {
      setAchievementPromptCount(readAchievementPrompt());
    });
    // Re-read on mount too, in case a celebration closed before this view mounted.
    setAchievementPromptCount(readAchievementPrompt());
    return unbind;
  }, []);
  const viewNewAchievements = () => {
    setAchievementsSheetOpen(true);
    clearAchievementPrompt();
    setAchievementPromptCount(undefined);
  };
  const dismissNewAchievements = () => {
    clearAchievementPrompt();
    setAchievementPromptCount(undefined);
  };

  // 法则二: when the phase task already routes to the matchday, the matchup
  // card's CTA is the single primary action and the task line is skipped.
  const matchdayIsLoop =
    primaryTask.kind !== "decision" &&
    nextGame !== undefined &&
    primaryTask.primaryAction.path[0] === "daily_schedule";
  // G2-3: the day-done stop point. When the matchday is the loop, today has no
  // game left to play (the user's next game is a future matchday), and nothing
  // dangerous is pending, the main task card becomes the "今日完成" state.
  const hasDanger =
    primaryTask.kind === "blocker" || taskHub.todos.some((item) => item.severity === "danger");
  const dayDone = getDashboardDayDone({
    hasDanger,
    matchdayIsLoop,
    nextGameDaysUntil: nextGame?.daysUntil,
  });
  const matchdayIsNow =
    matchdayIsLoop && nextGame?.daysUntil !== undefined && nextGame.daysUntil <= 0;
  const matchdayIsPrimary = !dayDone && matchdayIsNow;

  const primaryTaskSlot: ReactNode = dayDone ? (
    <div className="mb-3" key="primary-task">
      <DayDoneCard
        nextGame={nextGame!}
        onLongAdvance={recordAdvanceSnapshot}
        phase={phase}
        playMenuOptions={playMenuOptions}
        userTid={userTid}
      />
    </div>
  ) : matchdayIsPrimary ? null : (
    <div className="mb-3" key="primary-task">
      <DashboardTaskLine
        onOpenDecision={openInboxDecision}
        onLongAdvance={recordAdvanceSnapshot}
        phase={phase}
        playMenuOptions={playMenuOptions}
        primaryTask={primaryTask}
      />
    </div>
  );
  const phaseTaskIsDistinctFromPrimary =
    phaseTask !== undefined &&
    (primaryTask.kind === "decision" ||
      (primaryTask.kind === "blocker" &&
        (primaryTask.titleKey !== phaseTask.titleKey ||
          primaryTask.primaryAction.path.join("/") !== phaseTask.primaryAction.path.join("/"))));
  const statusSummarySlot: ReactNode = (
    <DashboardSeasonStatusSummary
      expectation={seasonExpectation}
      gamesPlayed={won + lost + tied + otl}
      goalState={goalState}
      key="status-summary"
      lastGame={lastGame}
      matchdayPrimary={matchdayIsPrimary}
      maxPlayoffSeed={maxPlayoffSeed}
      maxPlayoffSeedNoPlayIn={maxPlayoffSeedNoPlayIn}
      nextGame={nextGame}
      nextPhaseTask={phaseTaskIsDistinctFromPrimary ? phaseTask : undefined}
      playoffRoundsWon={playoffRoundsWon}
      rank={rank}
      record={snapshot.record}
      roundsWonText={roundsWonText}
      showMatchdayAction={
        primaryTask.kind !== "decision" && !dayDone && (!matchdayIsLoop || matchdayIsPrimary)
      }
      userTid={userTid}
    />
  );

  return (
    <div
      data-mobile-theme-contrast="content-on-surface"
      data-mobile-theme-surface="league-dashboard"
      data-v5-dashboard-play-option-ids={playMenuOptions.map((option) => option.id).join(" ")}
    >
      {statusSummarySlot}
      <DashboardTaskQueue
        mandatory={actionSectionMandatory}
        onOpenInboxDecision={openInboxDecision}
        overflowCount={0}
        primaryTaskSlot={primaryTaskSlot}
        todos={operationalTodos}
      />
      {/* G2-2: post-advance digest — retrospective context stays below current
          required work and self-hides when there is no diff. */}
      {advanceDigest && advanceSnapshot ? (
        <DashboardAdvanceDigestCard
          digest={advanceDigest}
          lid={advanceSnapshot.lid}
          onDismiss={dismissAdvanceDigest}
        />
      ) : null}
      {/* G7-1: one-time "new achievement" prompt after a celebration close. */}
      {achievementPromptCount !== undefined ? (
        <DashboardNewAchievementsCard
          count={achievementPromptCount}
          onDismiss={dismissNewAchievements}
          onView={viewNewAchievements}
        />
      ) : null}
      <AchievementsSheet
        onClose={() => {
          setAchievementsSheetOpen(false);
        }}
        open={achievementsSheetOpen}
      />
      <DashboardInformation
        onOpen={() => openInboxSheet()}
        ownerMood={ownerMood}
        unreadCount={unreadCount}
      />
      <LeagueNewsTicker news={news ?? []} />
      <DashboardTeamState
        injuredPlayers={injuredPlayers ?? 0}
        maxRosterSize={maxRosterSize}
        minRosterSize={minRosterSize}
        snapshot={snapshot}
      />
      <DashboardAgentAdvice
        contractStatus={resolvedContractStatus}
        salaryPressure={riskAlertsForTaskHub.some((alert) => alert.key === "salaryPressure")}
      />
      {/* U2-2: self-hiding statistical evidence stays secondary to the task queue. */}
      <DashboardStatInsightsBlock gamesPlayed={won + lost + tied + otl} teamStats={teamStats} />
      <InboxSheet
        initialFilter="unread"
        initialMid={inboxMid}
        onClose={() => setInboxOpen(false)}
        onMessageRead={markInformationMessageRead}
        open={inboxOpen}
        summaryHeader={ownerMood ? <OwnerTrustBlock ownerMood={ownerMood} /> : undefined}
        userTid={userTid}
      />
      {/* G4-2: the "决定" container — a pending owner letter pushed straight to the
          player as a centered card (home decision entry / ?m_open_latest). */}
      <OwnerLetterDialog
        mid={letterDialogMid}
        onClose={() => setLetterDialogOpen(false)}
        onMessageRead={markInformationMessageRead}
        open={letterDialogOpen}
      />
    </div>
  );
};

export default LeagueDashboard;
